/** Size of the round image in pixels: twice the size it is shown at, so it stays sharp. */
const roundImageSize = 400

/**
 * Crops an image to a circle, like the profile image in the PDF. Word cannot crop images
 * to a circle by itself, so the round image is made here as a PNG with transparent corners.
 * Non-square images are cropped from the center.
 *
 * @param {ArrayBuffer} image - The image file.
 * @returns {Promise<ArrayBuffer>} The round PNG, or the original image if the browser can't draw it.
 */
export const toRoundImage = async (image: ArrayBuffer): Promise<ArrayBuffer> => {
  try {
    const bitmap = await createImageBitmap(new Blob([image]))
    const canvas = document.createElement('canvas')
    canvas.width = roundImageSize
    canvas.height = roundImageSize
    const context = canvas.getContext('2d')
    if (!context) {
      return image
    }

    const side = Math.min(bitmap.width, bitmap.height)
    context.beginPath()
    context.arc(roundImageSize / 2, roundImageSize / 2, roundImageSize / 2, 0, 2 * Math.PI)
    context.clip()
    context.drawImage(
      bitmap,
      (bitmap.width - side) / 2,
      (bitmap.height - side) / 2,
      side,
      side,
      0,
      0,
      roundImageSize,
      roundImageSize
    )

    const png = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'))
    return png ? await png.arrayBuffer() : image
  } catch (error) {
    console.error(error)
    return image
  }
}
